import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useAxios from "../../hooks/useAxios";
import {
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import "./SessionPage.css";
import MathPreview from "../../components/CustomInputFields/MathPreview/MathPreview";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import CustomInputField from "../../components/CustomInputFields/CustomInputField/CustomInputField";
import Header from "../../components/Header/Header";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Tooltip from "@mui/material/Tooltip";
import useBrowserLock from "../../hooks/useBrowserLock";
import BrowserLockExitModal from "../../components/QuizComponents/Modals/BrowserLockExitModal/BrowserLockExitModal";
import QuizTimer from "../../components/QuizComponents/Timer/QuizTimer";
import QuestionNavigation from "../../components/QuizComponents/Navigation/QuestionNavigation";

const navLinks = [];
const SessionPage = () => {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const axiosInstance = useAxios();

  const [loading, setLoading] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(true);
  const [error, setError] = useState(null);

  const [questionType, setQuestionType] = useState(null); // 'question', 'subquestion', or null if finished
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentSubquestion, setCurrentSubquestion] = useState(null);

  const [response, setResponse] = useState("");
  const [attempt, setAttempt] = useState(1);
  const [feedback, setFeedback] = useState(null); // { is_correct: bool, hint: string, grade: number }

  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [finalResult, setFinalResult] = useState(null); // { final_grade, correct_answers, incorrect_answers, ... }

  const [isMathAnswer, setIsMathAnswer] = useState(false);
  const [hintsEnabled, setHintsEnabled] = useState(true);
  const [attemptLimit, setAttemptLimit] = useState(3);
  const [browserLockEnabled, setBrowserLockEnabled] = useState(false);

  const [questionChoices, setQuestionChoices] = useState([]);
  const [subQuestionChoices, setSubQuestionChoices] = useState([]);
  const [report, setReport] = useState(null);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [remainingTimeSeconds, setRemainingTimeSeconds] = useState(null);

  // Navigation state
  const [allQuestions, setAllQuestions] = useState([]);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [showNavigation, setShowNavigation] = useState(true);

  // Browser lock functionality
  const handleBrowserLockExit = () => {
    completeSession();
  };

  // Timer functionality
  const handleTimeUp = async () => {
    console.log("Time's up! Auto-submitting session...");
    await completeSession();
  };

  const {
    showExitModal,
    showFullscreenPrompt,
    isFullscreen,
    handleExitConfirm,
    handleExitCancel,
    handleManualFullscreen,
    handleDismissPrompt,
  } = useBrowserLock(browserLockEnabled, handleBrowserLockExit);

  // Debug logging
  useEffect(() => {
    console.log("SessionPage - browserLockEnabled:", browserLockEnabled);
    console.log("SessionPage - hintsEnabled:", hintsEnabled);
  }, [browserLockEnabled, hintsEnabled]);

  // Update remaining time every second
  useEffect(() => {
    if (!timeLimitMinutes || !sessionStartTime || sessionCompleted) return;

    const updateRemainingTime = () => {
      const startTime = new Date(sessionStartTime).getTime();
      const now = new Date().getTime();
      const elapsedSeconds = Math.floor((now - startTime) / 1000);
      const totalSeconds = timeLimitMinutes * 60;
      const remaining = Math.max(0, totalSeconds - elapsedSeconds);

      setRemainingTimeSeconds(remaining);

      // If time is up, trigger time up handler
      if (remaining <= 0) {
        handleTimeUp();
      }
    };

    // Update immediately
    updateRemainingTime();

    // Update every second
    const interval = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [timeLimitMinutes, sessionStartTime, sessionCompleted, handleTimeUp]);
  // Fetch quiz data to get browser lock setting and time limit
  const fetchQuizData = async () => {
    try {
      const response = await axiosInstance.get(
        `/sessions/${sessionId}/details`
      );
      if (response.data.success && response.data.quiz) {
        setBrowserLockEnabled(response.data.quiz.browser_lock || false);
        setHintsEnabled(response.data.quiz.hints_enabled !== false);

        const timeLimit = response.data.session?.time_limit_minutes;
        const sessionCreatedAt = response.data.session?.created_at;

        setTimeLimitMinutes(timeLimit || null);
        setSessionStartTime(sessionCreatedAt || null);

        // Calculate remaining time if we have both time limit and session start time
        if (timeLimit && sessionCreatedAt) {
          const startTime = new Date(sessionCreatedAt).getTime();
          const now = new Date().getTime();
          const elapsedSeconds = Math.floor((now - startTime) / 1000);
          const totalSeconds = timeLimit * 60;
          const remaining = Math.max(0, totalSeconds - elapsedSeconds);

          setRemainingTimeSeconds(remaining);
          console.log("Session started at:", sessionCreatedAt);
          console.log("Time limit:", timeLimit, "minutes");
          console.log("Elapsed time:", elapsedSeconds, "seconds");
          console.log("Remaining time:", remaining, "seconds");
        }

        console.log("Browser lock enabled:", response.data.quiz.browser_lock);
        console.log("Hints enabled:", response.data.quiz.hints_enabled);
        console.log(
          "Time limit minutes:",
          response.data.session?.time_limit_minutes
        );
      }
    } catch (err) {
      console.error("Error fetching quiz data:", err);
    }
  };

  // Fetch all questions for navigation
  const fetchAllQuestions = async () => {
    try {
      const response = await axiosInstance.get(
        `/sessions/${sessionId}/questions`
      );
      if (response.data.success) {
        setAllQuestions(response.data.questions || []);
        console.log("Fetched questions:", response.data.questions);
      }
    } catch (err) {
      console.error("Error fetching questions:", err);
    }
  };

  // Fetch a specific question by ID
  const fetchQuestionById = async (questionId) => {
    setQuestionLoading(true);
    setError(null);
    setFeedback(null);
    setResponse("");
    setAttempt(1);
    setQuestionChoices([]);
    setSubQuestionChoices([]);

    try {
      const res = await axiosInstance.get(
        `/sessions/${sessionId}/questions/${questionId}`
      );
      if (
        res.data.success &&
        (res.data.type === "question" || res.data.type === "subquestion")
      ) {
        setQuestionType(res.data.type);
        setCurrentQuestion(res.data.question);
        setCurrentSubquestion(res.data.subquestion || null);
        setCurrentQuestionId(questionId);

        // Parse question choices if present
        if (res.data.question && res.data.question.choices) {
          try {
            const parsed = JSON.parse(res.data.question.choices);
            if (Array.isArray(parsed)) {
              setQuestionChoices(parsed);
            }
          } catch (e) {
            console.error("Failed to parse question choices:", e);
          }
        }

        // Parse subquestion choices if present
        if (res.data.subquestion && res.data.subquestion.choices) {
          try {
            const parsed = JSON.parse(res.data.subquestion.choices);
            if (Array.isArray(parsed)) {
              setSubQuestionChoices(parsed);
            }
          } catch (e) {
            console.error("Failed to parse subquestion choices:", e);
          }
        }
      } else {
        // If no more questions, it means we reached the end
        setQuestionType(null);
      }
    } catch (err) {
      console.error("Error fetching question:", err);
      if (err.response?.data?.time_expired) {
        // Time has expired, auto-submit the session
        await completeSession();
      } else {
        setError(err.response?.data?.message || "Failed to load question.");
      }
    } finally {
      setQuestionLoading(false);
    }
  };

  // Fetch the next question or subquestion
  const fetchNextQuestion = async () => {
    setQuestionLoading(true);
    setError(null);
    setFeedback(null);
    setResponse("");
    setAttempt(1);
    setQuestionChoices([]);
    setSubQuestionChoices([]);

    try {
      const res = await axiosInstance.get(
        `/sessions/${sessionId}/next-question`
      );
      if (
        res.data.success &&
        (res.data.type === "question" || res.data.type === "subquestion")
      ) {
        setQuestionType(res.data.type);
        setCurrentQuestion(res.data.question);
        setCurrentSubquestion(res.data.subquestion || null);
        setCurrentQuestionId(res.data.question.id);

        // Parse question choices if present
        if (res.data.question && res.data.question.choices) {
          try {
            const parsed = JSON.parse(res.data.question.choices);
            if (Array.isArray(parsed)) {
              setQuestionChoices(parsed);
            }
          } catch (e) {
            console.error("Failed to parse question choices:", e);
          }
        }

        // Parse subquestion choices if present
        if (res.data.subquestion && res.data.subquestion.choices) {
          try {
            const parsed = JSON.parse(res.data.subquestion.choices);
            if (Array.isArray(parsed)) {
              setSubQuestionChoices(parsed);
            }
          } catch (e) {
            console.error("Failed to parse subquestion choices:", e);
          }
        }
      } else {
        // If no more questions, it means we reached the end
        setQuestionType(null);
      }
    } catch (err) {
      console.error("Error fetching next question:", err);
      if (err.response?.data?.time_expired) {
        // Time has expired, auto-submit the session
        await completeSession();
      } else {
        setError(err.response?.data?.message || "Failed to load question.");
      }
    } finally {
      setQuestionLoading(false);
    }
  };

  // Submit answer
  const submitAnswer = async () => {
    if (!currentQuestion) return;

    setLoading(true);
    setError(null);
    setFeedback(null);

    const questionId = currentQuestion.id;
    const subquestionId = currentSubquestion?.id;

    try {
      const payload = {
        response,
        attempt,
      };
      if (subquestionId) {
        payload.subquestion_id = subquestionId;
      }
      const res = await axiosInstance.post(
        `/sessions/${sessionId}/questions/${questionId}/answer`,
        payload
      );

      // After we get the response, show the feedback
      setFeedback({
        is_correct: res.data.is_correct,
        hint: hintsEnabled ? res.data.hint : null,
        grade: res.data.grade,
      });

      // Update hint mode and attempt limit from response
      if (res.data.hints_enabled !== undefined) {
        setHintsEnabled(res.data.hints_enabled);
      }
      if (res.data.attempt_limit !== undefined) {
        setAttemptLimit(res.data.attempt_limit);
      }

      // Refresh the questions list to update navigation
      fetchAllQuestions();
    } catch (err) {
      console.error("Error submitting answer:", err);
      if (err.response?.data?.time_expired) {
        // Time has expired, auto-submit the session
        await completeSession();
      } else {
        setError(err.response?.data?.error || "Failed to submit answer.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Complete the session and show final results
  const completeSession = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.post(`/sessions/${sessionId}/complete`);
      if (res.data.success) {
        setSessionCompleted(true);
        setFinalResult({
          final_grade: res.data.final_grade,
          correct_answers: res.data.correct_answers,
          incorrect_answers: res.data.incorrect_answers,
          total_questions: res.data.total_questions,
          total_main_questions: res.data.total_main_questions,
          total_subquestions: res.data.total_subquestions,
        });

        // <--- NEW: also store the AI report if present
        if (res.data.report) {
          setReport(res.data.report);
        }
      } else {
        setError(res.data.message || "Failed to complete session.");
      }
    } catch (err) {
      console.error("Error completing session:", err);
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // Handle question selection from navigation
  const handleQuestionSelect = (questionId) => {
    fetchQuestionById(questionId);
  };

  // On mount, fetch the first question
  useEffect(() => {
    fetchQuizData();
    fetchAllQuestions();
    fetchNextQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleNextQuestion = () => {
    // If the answer was correct or we simply want the next question:
    fetchNextQuestion();
    // Refresh the questions list to update navigation
    fetchAllQuestions();
  };

  const handleTryAgain = () => {
    // Increase attempt number and allow user to try again
    setAttempt((prev) => (prev < attemptLimit ? prev + 1 : prev));
    setFeedback(null);
  };

  const handleAnswerToggleChange = (event) => {
    setIsMathAnswer(event.target.checked);
  };

  const renderChoices = (choicesArray) => {
    return (
      <RadioGroup
        className="choices-container"
        value={response}
        onChange={(e) => setResponse(e.target.value)}
      >
        {choicesArray.map((choice, index) => (
          <FormControlLabel
            key={index}
            value={choice.value}
            control={<Radio />}
            label={<MathPreview text={choice.value} />}
          />
        ))}
      </RadioGroup>
    );
  };

  if (sessionCompleted) {
    // Show final result summary
    return (
      <>
        {/* No Header on session page for cleaner experience */}
        <div className="session-page-container page-content">
          <Card className="result-card">
            <CardContent>
              <Typography className="announcement" gutterBottom>
                Session Completed
              </Typography>
              <div className="results-container">
                {/* <div className="result-item">
                  <Typography className="results-key">Correct Answers:</Typography>
                  <Typography className="results-value">{finalResult.correct_answers}</Typography>
                </div>
                <div className="result-item">
                  <Typography className="results-key">Incorrect Answers:</Typography>
                  <Typography className="results-value">{finalResult.incorrect_answers}</Typography>
                </div> */}
                <div className="result-item centered">
                  <Typography className="results-key">Final Grade:</Typography>
                  <Typography className="results-value">
                    {finalResult.final_grade.toFixed(1)}%
                  </Typography>
                </div>
              </div>

              {/* AI Report Section */}
              {report && (
                <Box mt={4} className="ai-report-container">
                  <Typography
                    variant="h6"
                    gutterBottom
                    className="ai-report-title"
                  >
                    Your AI Performance Report
                  </Typography>
                  <div className="ai-report-section">
                    <Typography variant="subtitle1" className="report-label">
                      Strength Points:
                    </Typography>
                    <Typography variant="body1" className="report-text">
                      {report.strength_points || "N/A"}
                    </Typography>
                  </div>
                  <div className="ai-report-section">
                    <Typography variant="subtitle1" className="report-label">
                      Points to Improve:
                    </Typography>
                    <Typography variant="body1" className="report-text">
                      {report.points_to_improve || "N/A"}
                    </Typography>
                  </div>
                  <div className="ai-report-section">
                    <Typography variant="subtitle1" className="report-label">
                      Recommendation:
                    </Typography>
                    <Typography variant="body1" className="report-text">
                      {report.recommendation || "N/A"}
                    </Typography>
                  </div>
                </Box>
              )}

              <Button
                className="secondary-button centered-button"
                onClick={() => navigate("/dashboard")}
              >
                Go Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const hasQuestionChoices =
    Array.isArray(questionChoices) && questionChoices.length > 0;
  const hasSubQuestionChoices =
    Array.isArray(subQuestionChoices) && subQuestionChoices.length > 0;

  return (
    <>
      {/* No Header on session page for cleaner experience */}

      {/* Exit Button */}
      {/* {!sessionCompleted && (
        <Tooltip title="Exit Session" arrow>
          <Button
            className="session-exit-button"
            onClick={completeSession}
            variant="outlined"
            size="small"
          >
            <ArrowBackIcon />
          </Button>
        </Tooltip>
      )} */}

      {/* Quiz Timer */}
      {timeLimitMinutes && !sessionCompleted && (
        <QuizTimer
          timeLimitMinutes={timeLimitMinutes}
          remainingTimeSeconds={remainingTimeSeconds}
          onTimeUp={handleTimeUp}
          isActive={!sessionCompleted && !questionLoading}
        />
      )}

      {/* Question Navigation */}
      {showNavigation && !sessionCompleted && allQuestions.length > 0 && (
        <QuestionNavigation
          questions={allQuestions}
          currentQuestionId={currentQuestionId}
          onQuestionSelect={handleQuestionSelect}
        />
      )}

      <div className="session-page-container page-content">
        {questionLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="50vh"
          >
            <CircularProgress />
          </Box>
        )}

        {!questionLoading && questionType === null && !sessionCompleted && (
          <Card className="question-card">
            <CardContent>
              <Typography className="announcement" variant="h5" gutterBottom>
                Quiz Completed!
              </Typography>
              <Typography className="centered-text">
                Click below to complete the session and view your results.
              </Typography>
              <Button
                className="primary-button centered-button"
                onClick={completeSession}
                disabled={loading}
                style={{ marginTop: "1em" }}
              >
                {loading ? "Completing..." : "Complete Session"}
              </Button>
            </CardContent>
          </Card>
        )}

        {!questionLoading && questionType && (
          <Card className="question-card">
            <CardContent>
              {error && (
                <Alert severity="error" style={{ marginBottom: "1em" }}>
                  {error}
                </Alert>
              )}

              <Typography variant="h5" gutterBottom>
                {questionType === "subquestion" ? "Subquestion" : "Question"}
              </Typography>

              {/* Hint Mode Indicator */}
              {/* <div style={{
                backgroundColor: hintsEnabled ? '#e8f5e8' : '#fff3cd',
                padding: '10px',
                borderRadius: '6px',
                marginBottom: '15px',
                border: `1px solid ${hintsEnabled ? '#c3e6c3' : '#ffeaa7'}`
              }}>
                <Typography variant="body2" style={{ 
                  color: hintsEnabled ? '#2d5a2d' : '#856404',
                  fontWeight: '500'
                }}>
                  {hintsEnabled 
                    ? `💡 Hint Mode: You get ${attemptLimit} attempts with progressive hints`
                    : '📝 Strict Mode: You get 1 attempt with no hints'
                  }
                </Typography>
              </div> */}

              {currentQuestion && (
                <div className="question-container">
                  <Typography variant="body1">
                    <MathPreview text={currentQuestion?.body} />
                  </Typography>
                  {currentQuestion?.image && (
                    <img
                      src={currentQuestion?.image}
                      alt="question"
                      className="question-image"
                    />
                  )}

                  {hasQuestionChoices && renderChoices(questionChoices)}
                </div>
              )}

              {currentSubquestion && (
                <div
                  className="question-container"
                  style={{ marginTop: "1em" }}
                >
                  <Typography variant="body1">
                    <strong>Subquestion:</strong>{" "}
                    <MathPreview text={currentSubquestion.body} />
                  </Typography>
                  {currentSubquestion?.image && (
                    <img
                      src={currentSubquestion.image}
                      alt="subquestion"
                      className="question-image"
                    />
                  )}

                  {hasSubQuestionChoices && renderChoices(subQuestionChoices)}
                </div>
              )}
              {feedback && (
                <Alert
                  severity={feedback.is_correct ? "success" : "warning"}
                  style={{ marginBottom: "1em" }}
                >
                  {feedback.is_correct ? "Correct!" : "Incorrect"}
                  {feedback.hint && <div>Hint: {feedback.hint}</div>}
                  {typeof feedback.grade === "number" && (
                    <div>Grade: {feedback.grade.toFixed(1)}</div>
                  )}
                </Alert>
              )}

              {!feedback && !hasQuestionChoices && !hasSubQuestionChoices && (
                <div className="answer-form">
                  <ToggleSwitch
                    mainLabel="answer mode"
                    labelLeft="Regular"
                    labelRight="Math"
                    checked={isMathAnswer}
                    onChange={handleAnswerToggleChange}
                  />
                  {isMathAnswer ? (
                    <CustomInputField
                      body={response}
                      onBodyChange={(value) => setResponse(value)}
                      label={"Your Answer"}
                      isbigger={true}
                    />
                  ) : (
                    <TextField
                      label="Your Answer"
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      fullWidth
                      multiline
                      rows={3}
                      className="custom-text-field"
                      style={{ marginBottom: "1em" }}
                    />
                  )}

                  <Button
                    onClick={submitAnswer}
                    disabled={loading || !response}
                    className="primary-button"
                  >
                    {loading ? "Submitting..." : "Submit Answer"}
                  </Button>
                </div>
              )}

              {!feedback && (hasQuestionChoices || hasSubQuestionChoices) && (
                <div style={{ marginTop: "1em" }}>
                  <Button
                    className="primary-button centered-button"
                    onClick={submitAnswer}
                    disabled={loading || !response}
                  >
                    {loading ? "Submitting..." : "Submit Answer"}
                  </Button>
                </div>
              )}

              {feedback && (
                <div
                  className="answer-result-actions"
                  style={{ marginTop: "1em" }}
                >
                  {feedback.is_correct ? (
                    <Button
                      className="secondary-button centered-button"
                      onClick={handleNextQuestion}
                    >
                      Next Question
                    </Button>
                  ) : hintsEnabled && attempt < attemptLimit ? (
                    <Button
                      className="secondary-button centered-button"
                      onClick={handleTryAgain}
                    >
                      Try Again (Attempt {attempt + 1})
                    </Button>
                  ) : (
                    <Button
                      className="secondary-button centered-button"
                      onClick={handleNextQuestion}
                    >
                      Next Question
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Floating Complete Session Button */}
        <div className="floating-complete-session">
          <Tooltip title="End Session and Exit" arrow>
            <Button
              className="exit-session-floating-button"
              onClick={completeSession}
              disabled={loading || sessionCompleted}
            >
              Exit
            </Button>
          </Tooltip>
        </div>

        {/* Browser Lock Exit Modal */}
        <BrowserLockExitModal
          open={showExitModal}
          onClose={handleExitCancel}
          onConfirm={handleExitConfirm}
          loading={loading}
        />

        {/* Fullscreen Prompt Modal */}
        {showFullscreenPrompt && (
          <div className="fullscreen-prompt-overlay">
            <div className="fullscreen-prompt-modal">
              <div className="fullscreen-prompt-content">
                <h3>Fullscreen Required</h3>
                <p>
                  This quiz requires fullscreen mode for security. Please click
                  the button below to enter fullscreen mode.
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ed6c02", // MUI warning.main
                    backgroundColor: "#fff3cd", // MUI warning.light
                    border: "1px solid #ed6c02",
                    borderRadius: "6px",
                    padding: "10px 16px",
                    marginTop: "1.2em",
                    fontWeight: 500,
                    fontSize: "1em",
                    marginBottom: "1.2em",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: "8px",
                      fontSize: "1.5em",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="1.5em"
                      viewBox="0 0 24 24"
                      width="1.5em"
                      fill="#ed6c02"
                      style={{ marginRight: "4px" }}
                    >
                      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
                    </svg>
                  </span>
                  Exiting fullscreen will terminate the session and finish the
                  exam.
                </div>
                <div className="fullscreen-prompt-actions">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleManualFullscreen}
                    style={{ marginRight: "10px" }}
                  >
                    Enter Fullscreen
                  </Button>
                  {/* <Button variant="outlined" onClick={handleDismissPrompt}>
                    Continue Without Fullscreen
                  </Button> */}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SessionPage;
